import React, { useState, useEffect, useCallback } from 'react';
import {
  Store,
  RefreshCw,
  Eye,
  Search,
  TrendingUp,
  ShoppingCart,
  Banknote,
  CreditCard,
  Building2,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Users,
  DollarSign,
  BarChart3
} from 'lucide-react';
import './POSManagement.css';

const POSManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({
    merchantId: '',
    paymentMethod: '',
    paymentStatus: '',
    startDate: '',
    endDate: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const loadSummary = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const response = await fetch(`/api/admin/pos/summary?${queryParams}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setSummary(data.summary);
      }
    } catch (err) {
      console.error('Failed to load POS summary:', err);
    }
  }, [filters.startDate, filters.endDate]);

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const queryParams = new URLSearchParams({
        page: pagination.current,
        limit: 20
      });

      if (filters.merchantId) queryParams.append('merchantId', filters.merchantId);
      if (filters.paymentMethod) queryParams.append('paymentMethod', filters.paymentMethod);
      if (filters.paymentStatus) queryParams.append('paymentStatus', filters.paymentStatus);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.search) queryParams.append('search', filters.search);

      const response = await fetch(`/api/admin/pos/transactions?${queryParams}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setTransactions(data.orders || []);
        if (data.pagination) {
          setPagination(prev => ({
            ...prev,
            pages: data.pagination.pages || 1,
            total: data.pagination.total || 0
          }));
        }
      } else {
        setError(data.error || 'Failed to load transactions');
      }
    } catch (err) {
      setError('Failed to load POS transactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [pagination.current, filters]);

  const loadMerchants = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/pos/merchants', {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setMerchants(data.merchants || []);
      }
    } catch (err) {
      console.error('Failed to load POS merchants:', err);
    }
  }, []);

  const viewOrderDetails = async (orderId) => {
    try {
      const response = await fetch(`/api/admin/pos/order/${orderId}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setSelectedOrder(data.order);
        setShowModal(true);
      }
    } catch (err) {
      console.error('Failed to load order details:', err);
    }
  };

  useEffect(() => {
    loadSummary();
    loadMerchants();
  }, [loadSummary, loadMerchants]);

  useEffect(() => {
    if (activeTab === 'transactions') {
      loadTransactions();
    }
  }, [activeTab, loadTransactions]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPagination(prev => ({ ...prev, current: newPage }));
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'CASH': return <Banknote size={16} />;
      case 'BANK_TRANSFER': return <Building2 size={16} />;
      case 'CARD': return <CreditCard size={16} />;
      default: return <DollarSign size={16} />;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PAID: { class: 'posm-badge-success', icon: <CheckCircle2 size={14} /> },
      PENDING: { class: 'posm-badge-warning', icon: <Clock size={14} /> },
      FAILED: { class: 'posm-badge-danger', icon: <XCircle size={14} /> }
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <span className={`posm-badge ${config.class}`}>
        {config.icon}
        <span>{status}</span>
      </span>
    );
  };

  const renderOverview = () => (
    <div className="posm-overview">
      {/* Summary Cards */}
      <div className="posm-stats-grid">
        <div className="posm-stat-card" data-type="primary">
          <div className="posm-stat-icon" data-type="primary">
            <ShoppingCart size={24} />
          </div>
          <div className="posm-stat-content">
            <h3>{summary?.totalOrders || 0}</h3>
            <p>Total POS Orders</p>
          </div>
        </div>

        <div className="posm-stat-card" data-type="success">
          <div className="posm-stat-icon" data-type="success">
            <DollarSign size={24} />
          </div>
          <div className="posm-stat-content">
            <h3>{formatCurrency(summary?.paidAmount)}</h3>
            <p>Total Paid Amount</p>
          </div>
        </div>

        <div className="posm-stat-card" data-type="warning">
          <div className="posm-stat-icon" data-type="warning">
            <Clock size={24} />
          </div>
          <div className="posm-stat-content">
            <h3>{summary?.pendingOrders || 0}</h3>
            <p>Pending Payments</p>
          </div>
        </div>

        <div className="posm-stat-card" data-type="highlight">
          <div className="posm-stat-icon" data-type="info">
            <TrendingUp size={24} />
          </div>
          <div className="posm-stat-content">
            <h3>{formatCurrency(summary?.todaySales)}</h3>
            <p>Today's POS Sales</p>
          </div>
        </div>
      </div>

      {/* Payment Methods Breakdown */}
      <div className="posm-section">
        <div className="posm-section-header">
          <h2 className="posm-section-title">
            <BarChart3 size={20} />
            Payment Methods
          </h2>
        </div>
        <div className="posm-section-body">
          <div className="posm-payment-grid">
            {summary?.byPaymentMethod?.map(pm => (
              <div key={pm.method} className="posm-payment-card">
                <div className="posm-payment-icon">
                  {getPaymentMethodIcon(pm.method)}
                </div>
                <div className="posm-payment-info">
                  <h4>{pm.method?.replace('_', ' ') || 'Unknown'}</h4>
                  <p className="posm-payment-count">{pm.count} orders</p>
                  <p className="posm-payment-total">{formatCurrency(pm.total)}</p>
                </div>
              </div>
            ))}
            {(!summary?.byPaymentMethod || summary.byPaymentMethod.length === 0) && (
              <div className="posm-empty">
                <BarChart3 size={40} />
                <p>No payment data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Merchants */}
      <div className="posm-section">
        <div className="posm-section-header">
          <h2 className="posm-section-title">
            <Users size={20} />
            Top POS Merchants
          </h2>
        </div>
        <div className="posm-section-body">
          <div className="posm-table-wrapper">
            <table className="posm-table">
              <thead>
                <tr>
                  <th>Merchant</th>
                  <th>Store</th>
                  <th>Orders</th>
                  <th>Total Sales</th>
                </tr>
              </thead>
              <tbody>
                {summary?.topMerchants?.map((tm, index) => (
                  <tr key={tm.merchantId || index}>
                    <td>
                      <div className="posm-merchant-cell">
                        <span className="posm-rank" data-rank={index + 1 <= 3 ? index + 1 : null}>
                          #{index + 1}
                        </span>
                        <span>{tm.merchant?.email || tm.merchant?.username || 'Unknown'}</span>
                      </div>
                    </td>
                    <td>{tm.merchant?.store?.name || '-'}</td>
                    <td>{tm._count}</td>
                    <td className="posm-amount">{formatCurrency(tm._sum?.total)}</td>
                  </tr>
                ))}
                {(!summary?.topMerchants || summary.topMerchants.length === 0) && (
                  <tr>
                    <td colSpan="4">
                      <div className="posm-empty">
                        <Users size={40} />
                        <p>No POS activity yet</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Merchants Using POS */}
      <div className="posm-section">
        <div className="posm-section-header">
          <h2 className="posm-section-title">
            <Store size={20} />
            Merchants Using POS ({merchants.length})
          </h2>
        </div>
        <div className="posm-section-body">
          <div className="posm-merchants-grid">
            {merchants.map((m, index) => (
              <div key={m.merchant?.id || index} className="posm-merchant-card">
                <div className="posm-merchant-header">
                  <Store size={20} />
                  <h4>{m.merchant?.store?.name || 'Unnamed Store'}</h4>
                </div>
                <div className="posm-merchant-details">
                  <p><strong>Owner:</strong> {m.merchant?.email}</p>
                  <p><strong>Tier:</strong> {m.merchant?.merchantTier || 'FREE'}</p>
                </div>
                <div className="posm-merchant-stats">
                  <span>{m.posStats?.totalOrders || 0} orders</span>
                  <span>{formatCurrency(m.posStats?.totalSales)}</span>
                </div>
              </div>
            ))}
            {merchants.length === 0 && (
              <div className="posm-empty">
                <Store size={48} />
                <p>No merchants using POS yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderTransactions = () => (
    <div className="posm-section">
      <div className="posm-section-body">
        {/* Filters */}
        <div className="posm-filters">
          <div className="posm-search">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search order number..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <select
            className="posm-select"
            value={filters.paymentMethod}
            onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
          >
            <option value="">All Payment Methods</option>
            <option value="CASH">Cash</option>
            <option value="BANK_TRANSFER">Bank Transfer</option>
            <option value="CARD">Card</option>
          </select>

          <select
            className="posm-select"
            value={filters.paymentStatus}
            onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="PAID">Paid</option>
            <option value="PENDING">Pending</option>
          </select>

          <div className="posm-date-filter">
            <Calendar size={16} />
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
            <span>to</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>

          <button className="posm-refresh-btn" onClick={loadTransactions} disabled={loading}>
            <RefreshCw size={18} className={loading ? 'posm-spin' : ''} />
          </button>
        </div>

        {/* Error Banner */}
        {error && <div className="posm-error">{error}</div>}

        {/* Transactions Table */}
        <div className="posm-table-wrapper">
          <table className="posm-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Store</th>
                <th>Date</th>
                <th>Items</th>
                <th>Amount</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8">
                    <div className="posm-loading">
                      <RefreshCw size={24} className="posm-spin" />
                      <p>Loading transactions...</p>
                    </div>
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan="8">
                    <div className="posm-empty">
                      <Store size={48} />
                      <p>No POS transactions found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                transactions.map(order => (
                  <tr key={order.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 500, color: 'var(--posm-primary)' }}>
                      {order.orderNumber}
                    </td>
                    <td>{order.store?.name || '-'}</td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>{Array.isArray(order.items) ? order.items.length : 0} items</td>
                    <td className="posm-amount">{formatCurrency(order.total)}</td>
                    <td>
                      <span className="posm-payment-badge">
                        {getPaymentMethodIcon(order.paymentMethod)}
                        {order.paymentMethod?.replace('_', ' ')}
                      </span>
                    </td>
                    <td>{getStatusBadge(order.paymentStatus)}</td>
                    <td>
                      <button
                        className="posm-action-btn"
                        onClick={() => viewOrderDetails(order.id)}
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="posm-pagination">
            <button
              className="posm-pagination-btn"
              onClick={() => handlePageChange(pagination.current - 1)}
              disabled={pagination.current === 1}
            >
              <ChevronLeft size={18} />
            </button>
            <span className="posm-pagination-info">
              Page {pagination.current} of {pagination.pages}
              <small>({pagination.total} total)</small>
            </span>
            <button
              className="posm-pagination-btn"
              onClick={() => handlePageChange(pagination.current + 1)}
              disabled={pagination.current === pagination.pages}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="posm-container">
      {/* Header */}
      <div className="posm-header">
        <div className="posm-header-left">
          <div className="posm-header-icon">
            <Store size={28} />
          </div>
          <div className="posm-header-text">
            <h1>POS Oversight</h1>
            <p>Monitor Point of Sale transactions across all merchants</p>
          </div>
        </div>
        <div className="posm-header-actions">
          <button className="posm-btn posm-btn-secondary" onClick={() => { loadSummary(); loadMerchants(); }}>
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="posm-tabs">
        <button
          className={`posm-tab ${activeTab === 'overview' ? 'posm-tab-active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BarChart3 size={18} />
          Overview
        </button>
        <button
          className={`posm-tab ${activeTab === 'transactions' ? 'posm-tab-active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          <ShoppingCart size={18} />
          Transactions
        </button>
      </div>

      {/* Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'transactions' && renderTransactions()}

      {/* Order Details Modal */}
      {showModal && selectedOrder && (
        <div className="posm-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="posm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="posm-modal-header">
              <h2>
                <ShoppingCart size={20} />
                Order Details
              </h2>
              <button className="posm-modal-close" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>
            <div className="posm-modal-body">
              <div className="posm-detail-grid">
                <div className="posm-detail-item">
                  <label>Order Number</label>
                  <p>{selectedOrder.orderNumber}</p>
                </div>
                <div className="posm-detail-item">
                  <label>Store</label>
                  <p>{selectedOrder.store?.name || '-'}</p>
                </div>
                <div className="posm-detail-item">
                  <label>Date</label>
                  <p>{formatDate(selectedOrder.createdAt)}</p>
                </div>
                <div className="posm-detail-item">
                  <label>Payment Method</label>
                  <p className="posm-payment-badge">
                    {getPaymentMethodIcon(selectedOrder.paymentMethod)}
                    {selectedOrder.paymentMethod?.replace('_', ' ')}
                  </p>
                </div>
                <div className="posm-detail-item">
                  <label>Payment Status</label>
                  {getStatusBadge(selectedOrder.paymentStatus)}
                </div>
                <div className="posm-detail-item">
                  <label>Total</label>
                  <p className="posm-amount-lg">{formatCurrency(selectedOrder.total)}</p>
                </div>
              </div>

              {/* Customer Info */}
              {selectedOrder.customerInfo && (
                <div className="posm-modal-section">
                  <h3>Customer Information</h3>
                  <div className="posm-customer-details">
                    <p><strong>Name:</strong> {selectedOrder.customerInfo.name || 'Walk-in'}</p>
                    {selectedOrder.customerInfo.phone && (
                      <p><strong>Phone:</strong> {selectedOrder.customerInfo.phone}</p>
                    )}
                    {selectedOrder.customerInfo.email && (
                      <p><strong>Email:</strong> {selectedOrder.customerInfo.email}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Order Items */}
              <div className="posm-modal-section">
                <h3>Items</h3>
                <table className="posm-items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(selectedOrder.items) && selectedOrder.items.map((item, index) => (
                      <tr key={index}>
                        <td>
                          {item.name}
                          {item.variantName && <small> ({item.variantName})</small>}
                        </td>
                        <td>{item.quantity}</td>
                        <td>{formatCurrency(item.price)}</td>
                        <td>{formatCurrency(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3">Subtotal</td>
                      <td>{formatCurrency(selectedOrder.subtotal)}</td>
                    </tr>
                    {selectedOrder.discount > 0 && (
                      <tr className="posm-discount-row">
                        <td colSpan="3">Discount</td>
                        <td>-{formatCurrency(selectedOrder.discount)}</td>
                      </tr>
                    )}
                    <tr className="posm-total-row">
                      <td colSpan="3"><strong>Total</strong></td>
                      <td><strong>{formatCurrency(selectedOrder.total)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Metadata */}
              {selectedOrder.metadata && (
                <div className="posm-modal-section">
                  <h3>Additional Info</h3>
                  <div className="posm-metadata-details">
                    <p><strong>Cashier:</strong> {selectedOrder.metadata.cashier || '-'}</p>
                    {selectedOrder.metadata.notes && (
                      <p><strong>Notes:</strong> {selectedOrder.metadata.notes}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POSManagement;
